<script setup>
/*
ClockFormPickTime.vue
Form to pick the date and time of a clock.
*/
import { ref, watch, onUnmounted } from "vue";
import Alert from "@/services/Alert";
import Helper from "@/services/Helper";
import KtInput from "@/components/UI/KtInput.vue";

// Define props
const props = defineProps({
    request: {
        type: Object,
        default: null
    },
    
    client: {
        type: Object,
        default: null
    },
    
    task: {
        type: Object,
        default: null
    },

    mode: {
        type: String,
        default: "start"
    },
    
    date: {
        type: String,
        default: ""
    },

    time: {
        type: String,
        default: ""
    }
    
});

// Define emits
const emits = defineEmits([
    "cancel",
    "back",
    "ok",
    "stop"
]);

const date = ref(Helper.dateToInput(new Date(), Helper.DateFormat_Date));
const time = ref(Helper.dateToInput(new Date(), Helper.DateFormat_Time));

// passed in properties take preference ...
const unwatch = [
    watch(() => props.date, (nv, ov) =>
    {
        if (nv)
        {
            date.value = nv;
        }
    }, 
    {
        immediate: true
    }),
    watch(() => props.time, (nv, ov) =>
    {
        if (nv)
        {
            time.value = nv;
        }
    },
    {
        immediate: true
    })
];


// Handle OK button click with validation
const ok = () => 
{

    if (!date.value || !time.value) {
        Alert.notify("Please select a date & time.");
        return;
    }

    let selectedTime = new Date(date.value + " " + time.value);

    // Ensure the selected date-time is not in the future
    const now = new Date();
    if (now < selectedTime) {
        Alert.notify("Please select a valid date & time in the past.");
        return;
    }

    // Emit the combined date-time
    if (props.mode === "stop") {
        emits("stop", selectedTime);
    }

    else
    {
        emits("ok", selectedTime);
    }
};

/* Cleanup watchers on unmount */
onUnmounted(() => 
{
    unwatch.forEach((unwatchFn) => unwatchFn());
});


</script>

<template>
    <div class="row">
        <div class="col">
            <!-- Client and Job Information -->
            <div class="row">
                <div class="col mx-auto">
                    <strong>Client:</strong> {{ props.client.Company }}<br />
                    <strong>Job:</strong> {{ props.request.TaskDetail || props.request.RequestDetailedDescription }}
                    <span v-if="props.task?.WorkAssignment">
                        <br />
                        <strong>Task:</strong> {{ props.task.WorkAssignment }}
                    </span>
                </div>
            </div>

            <hr />

            <!-- Date Input -->
            <div class="row">
                <div class="col">
                    <kt-input
                        type="date"
                        label="Date"
                        placeholder="Choose date"
                        v-model="date"
                    ></kt-input>
                </div>
            </div>

            <!-- Time Input -->
            <div class="row my-2">
                <div class="col">
                    <kt-input
                        type="time"
                        label="Time"
                        placeholder="Choose time"
                        v-model="time"
                    ></kt-input>
                </div>
            </div>

            <!-- Buttons -->
            <div class="row mt-3">
                <div class="col-md-6">
                    <!-- only show back if we're starting a clock -->
                    <button
                        v-if="props.mode == 'start'"
                        class="btn btn-secondary form-control"
                        @click="$emit('back')"
                    >
                        <span>Back</span>
                    </button>

                    <!-- only show cancel if we're stopping a clock -->
                    <button
                        v-if="props.mode != 'start'"
                        class="btn btn-secondary form-control"
                        @click="$emit('cancel')"
                    >
                        <span>Cancel</span>
                    </button>
                </div>
                <div class="col-md-6">
                    <button
                        class="btn btn-primary form-control"
                        @click="ok"
                    >
                        <span>OK</span>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>